import React, {useEffect, useState} from 'react';
import {Alert, Button, Modal} from 'react-bootstrap';
import {Bot} from 'lucide-react';
import {getReconciliationWork,getReconciliationTranscript,startReconciliationWork} from '../../../../services/ReconciliationActionService';
import ReconciliationQuestion from './ReconciliationQuestion';

const labels={queued:'Mats står i kö',investigating:'Mats undersöker händelsen',ready:'Mats har förberett ett förslag',executing:'Mats kontrollerar bokföringsresultatet',
    completed:'Mats har verifierat resultatet',waiting_input:'Mats behöver information',waiting_approval:'Mats väntar på ditt godkännande',
    waiting_evidence:'Mats väntar på underlag',blocked:'Mats kan inte gå vidare, hantera manuellt',paused:'Automatiken är pausad',retry_due:'Mats återhämtar ärendet',
    resolved_elsewhere:'Händelsen hanterades på annat sätt',cancelled:'Ärendet avslutades'};
// Only these states change on their own; everything else waits for the user.
const ACTIVE_STATES=['queued','investigating','ready','executing','retry_due'];
const POLL_MS=15000;

const linkStyle={fontSize:'0.8125rem',textDecoration:'none'};

export function ReconciliationTranscriptLink({companyId,workId,label='Visa samtal med Mats'}) {
    const [open,setOpen]=useState(false),[messages,setMessages]=useState([]),[error,setError]=useState('');
    const show=async()=>{setOpen(true);setError('');try {const r=await getReconciliationTranscript(companyId,workId);setMessages(r.messages||[]);}catch{setError('Samtalet kunde inte hämtas.');}};
    return <><Button variant="link" size="sm" className="p-0 text-muted" style={linkStyle} onClick={show}>{label}</Button>
        <Modal show={open} onHide={()=>setOpen(false)}><Modal.Header closeButton><Modal.Title as="h6">Samtal med Mats</Modal.Title></Modal.Header>
            <Modal.Body>{error && <Alert variant="warning">{error}</Alert>}{messages.map((m,i)=><div key={m.id||i} className="mb-3" style={{fontSize:'0.8125rem'}}><strong>{m.role==='user'?'Du':'Mats'}</strong><p style={{whiteSpace:'pre-wrap',marginBottom:0}}>{m.content}</p></div>)}</Modal.Body>
        </Modal></>;
}

/**
 * Reads the agent case for one bank line. Polls only while Mats is actually
 * working; every other state changes because the user did something.
 */
export function useReconciliationWork(companyId,line,enabled=true) {
    const [data,setData]=useState(null),[unavailable,setUnavailable]=useState(false),[busy,setBusy]=useState(false);
    const state=data?.work?.state;
    useEffect(()=>{
        if(!enabled)return undefined;
        let active=true;setData(null);setUnavailable(false);
        getReconciliationWork(companyId,line.id).then(r=>{if(active){setData(r);}}).catch(()=>{if(active)setUnavailable(true);});
        return()=>{active=false;};
    },[companyId,line.id,enabled]);
    useEffect(()=>{
        if(!enabled || !ACTIVE_STATES.includes(state))return undefined;
        let active=true;
        const timer=setInterval(()=>getReconciliationWork(companyId,line.id).then(r=>{if(active)setData(r);}).catch(()=>{if(active)setUnavailable(true);}),POLL_MS);
        return()=>{active=false;clearInterval(timer);};
    },[companyId,line.id,enabled,state]);
    const start=async()=>{setBusy(true);try{setData(await startReconciliationWork(companyId,line.id));setUnavailable(false);}catch{setUnavailable(true);}finally{setBusy(false);}};
    return {data,unavailable,busy,start};
}

/**
 * One line in the bank sheet: what Mats is doing with this bank line, and the
 * one thing the user can do about it. Renders nothing when there is no case —
 * a line the user reaches by hand is a line Mats did not handle, so his
 * absence needs no announcement.
 */
export default function ReconciliationWorkStatus({companyId,line,state}) {
    const own=useReconciliationWork(companyId,line,!state);
    const {data,unavailable,busy,start}=state||own;
    const work=data?.work;
    const outcome=data?.outcome;
    const committed=outcome?.status==='committed';
    const disposition=data?.bank?.disposition||line.reconciliation_status;
    const settled=['verified','skipped','reconciled_external'].includes(disposition);
    // A committed backend outcome the list has not caught up with yet.
    const outcomeText=committed && (outcome.created_payment?'Betalningen är registrerad.':outcome.created_verification?'Köpet är bokfört.':'Bokföringen är kopplad.');
    const recovering=work && (work.state==='retry_due'||work.state==='executing'||work.reason==='APPROVAL_PROCESSING');
    if(!work && !committed)return null;
    const statusText=data?.agent_unavailable||unavailable?'Mats är tillfälligt otillgänglig. Du kan fortsätta manuellt.':work?(labels[work.state]||'Ärendet uppdateras'):null;
    const showQuestion=!committed && !settled && work?.state==='waiting_input' && data.question
        && (line.reconciliation_generation==null || Number(line.reconciliation_generation)===work.generation);
    return <div aria-label="Mats avstämning" style={{display:'flex',alignItems:'flex-start',gap:8,marginTop:22,fontSize:'0.8125rem',color:'#374151'}}>
        <Bot size={14} style={{color:'#6b7280',flexShrink:0,marginTop:3}}/>
        <div style={{flex:1,minWidth:0}}>
            {outcomeText && line.reconciliation_status!=='verified' && <div role="status">{outcomeText}</div>}
            {statusText && <div role="status" className="d-flex flex-wrap align-items-baseline" style={{columnGap:10}}>
                <span>{statusText}</span>
                {recovering && <span style={{color:'#6b7280'}}>Kontrollerar om åtgärden redan är genomförd.</span>}
                {work && <ReconciliationTranscriptLink companyId={companyId} workId={work.id} label="Visa samtal"/>}
            </div>}
            {showQuestion && <ReconciliationQuestion key={data.question.questionId} companyId={companyId} workId={work.id} question={data.question}/>}
            {work?.state==='waiting_approval' && <a href={`/${companyId}/todos`} style={linkStyle}>Visa förslaget i Att göra</a>}
            {['paused','waiting_evidence','blocked'].includes(work?.state) && <Button variant="link" size="sm" className="p-0" style={linkStyle} disabled={busy} onClick={start}>Låt Mats undersöka igen</Button>}
        </div>
    </div>;
}
